import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { createTheme } from "@mui/material/styles";
import {
  Button,
  Grid,
  ThemeProvider,
  StyledEngineProvider,
  ListSubheader,
  MenuItem,
  InputAdornment,
  IconButton,
  Select,
  TextField as MuiTextField,
} from "@mui/material";
import { withStyles } from "@mui/styles";
import { Cancel } from "@mui/icons-material";
import { Tabs, Tab } from "../../volunteer_dashboard/domains/global/Tabs";
import TextField from "../../volunteer_dashboard/domains/global/TextField";
import theme from "../../volunteer_dashboard/domains/global/theme.json";
import * as actions from "./actions";
import { connectShift } from "./context";
import { FrequencyForm } from "./FrequencyForm";

const StyledInput = withStyles({
  root: {
    background: "transparent",
    "& input": { background: "white" },
  },
})(TextField);

export const ShiftForm = ({
  id,
  startDate,
  startTime,
  endTime,
  rrule,
  customText,
  tasks = [],
  users,
  dispatch,
}) => {
  const { t } = useTranslation();
  useEffect(() => {
    dispatch(actions.fetchOrgAndInitForm(id));
  }, [id]);
  const publicNotesEnField = useRef();
  const publicNotesEsField = useRef();
  const adminNotesEnField = useRef();
  const adminNotesEsField = useRef();
  const [publicNotesTabValue, setPublicNotesTabValue] = useState(0);
  const [adminNotesTabValue, setAdminNotesTabValue] = useState(0);
  const [publicNotesEn, setPublicNotesEn] = useState("");
  const [publicNotesEs, setPublicNotesEs] = useState("");
  const [adminNotesEn, setAdminNotesEn] = useState("");
  const [adminNotesEs, setAdminNotesEs] = useState("");
  useEffect(() => {
    publicNotesEnField.current.addEventListener("trix-change", (event) => {
      setPublicNotesEn(event.target.innerHTML);
    });
  }, [publicNotesTabValue]);
  useEffect(() => {
    publicNotesEsField.current.addEventListener("trix-change", (event) => {
      setPublicNotesEs(event.target.innerHTML);
    });
  }, [publicNotesTabValue]);
  useEffect(() => {
    adminNotesEnField.current.addEventListener("trix-change", (event) => {
      setAdminNotesEn(event.target.innerHTML);
    });
  }, [adminNotesTabValue]);
  useEffect(() => {
    adminNotesEsField.current.addEventListener("trix-change", (event) => {
      setAdminNotesEs(event.target.innerHTML);
    });
  }, [adminNotesTabValue]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [name, setName] = useState("");
  const [volunteerMinimum, setVolunteerMinimum] = useState(1);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const onCancel = () => {
    // TODO replace with react-navigation
    window.history.back();
  };
  const onFormSubmit = (e) => {
    setSubmitDisabled(true);
    e.preventDefault();
    const data = {
      id,
      name,
      shiftDate: startDate,
      startTime,
      endTime,
      rrule,
      task_ids: selectedTasks.map((t) => t.id),
      user_ids: selectedUsers.map((v) => v.id),
      volunteerMinimum,
      publicNotesEn,
      publicNotesEs,
      adminNotesEn,
      adminNotesEs,
    };
    dispatch(actions.submitFormData(data));
  };

  const unselectedTasks = tasks.filter((task) => !selectedTasks.includes(task));
  const activeTasks = unselectedTasks.filter((task) => !task.inactive);
  const inactiveTasks = unselectedTasks.filter((task) => task.inactive);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={createTheme(theme)}>
        <form aria-label="form" onSubmit={onFormSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={8}>
              <StyledInput
                error={false}
                id="name"
                label={t("shiftForm.nameLabel")}
                variant="outlined"
                required
                helperText={t("global.fieldRequired")}
                value={name}
                onChange={setName}
              />
            </Grid>
            <Grid item xs={4} />
            <FrequencyForm
              rrule={rrule}
              dispatch={dispatch}
              customText={customText}
              endTime={endTime}
              startTime={startTime}
              startDate={startDate}
            />
            <Grid item xs={3}>
              <StyledInput
                error={false}
                id="volunteer_minimum"
                label={t("shiftForm.volunteerMinimumLabel")}
                variant="outlined"
                value={volunteerMinimum}
                onChange={setVolunteerMinimum}
              />
            </Grid>
            <Grid item xs={9} />
            <Grid
              container
              item
              xs={6}
              spacing={2}
              direction="column"
              style={{ alignContent: "start" }}
            >
              <Grid item>
                <h3>{t("shiftForm.tasksHeader")}</h3>
              </Grid>
              {selectedTasks.map((task) => (
                <Grid item key={task.id} style={{ width: "100%" }}>
                  <MuiTextField
                    id={task.description}
                    variant="outlined"
                    value={task.description}
                    label={t("shiftForm.taskLabel")}
                    style={{ width: "100%" }}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={t("shiftForm.tasksRemove")}
                            edge="end"
                            onClick={() => {
                              const ind = selectedTasks.findIndex(
                                (t) => t.id === task.id,
                              );
                              selectedTasks.splice(ind, 1);
                              setSelectedTasks([...selectedTasks]);
                            }}
                            size="large"
                          >
                            <Cancel />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              ))}
              <Grid item style={{ width: "100%" }}>
                <Select
                  id="addTask"
                  variant="outlined"
                  value={t("shiftForm.tasksMenuPlaceholder")}
                  style={{ width: "100%" }}
                  onChange={(v) =>
                    setSelectedTasks([...selectedTasks, v.target.value])
                  }
                >
                  <MenuItem value={t("shiftForm.tasksMenuPlaceholder")}>
                    {t("shiftForm.tasksMenuPlaceholder")}
                  </MenuItem>
                  <ListSubheader>{t("shiftForm.activeSites")}</ListSubheader>
                  {activeTasks.map((task) => (
                    <MenuItem key={task.id} value={task}>
                      {task.description}
                    </MenuItem>
                  ))}
                  <ListSubheader>{t("shiftForm.inactiveSites")}</ListSubheader>
                  {inactiveTasks.map((task) => (
                    <MenuItem key={task.id} value={task}>
                      {task.description}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>
            <Grid
              container
              item
              xs={6}
              spacing={2}
              direction="column"
              style={{ alignContent: "start" }}
            >
              <Grid item>
                <h3>{t("shiftForm.usersHeader")}</h3>
              </Grid>
              {selectedUsers.map((user) => (
                <Grid item key={user.id} style={{ width: "100%" }}>
                  <MuiTextField
                    id={user.fullNameOrEmail}
                    variant="outlined"
                    value={user.fullNameOrEmail}
                    label={t("shiftForm.userLabel")}
                    style={{ width: "100%" }}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={t("shiftForm.usersRemove")}
                            edge="end"
                            onClick={() => {
                              const ind = selectedUsers.findIndex(
                                (t) => t.id === user.id,
                              );
                              selectedUsers.splice(ind, 1);
                              setSelectedUsers([...selectedUsers]);
                            }}
                            size="large"
                          >
                            <Cancel />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              ))}
              <Grid item style={{ width: "100%" }}>
                <Select
                  id="addUser"
                  variant="outlined"
                  value={t("shiftForm.userMenuPlaceholder")}
                  style={{ width: "100%" }}
                  onChange={(v) =>
                    setSelectedUsers([...selectedUsers, v.target.value])
                  }
                >
                  <MenuItem value={t("shiftForm.userMenuPlaceholder")}>
                    {t("shiftForm.userMenuPlaceholder")}
                  </MenuItem>
                  {users
                    .filter((user) => !selectedUsers.includes(user))
                    .map((user) => (
                      <MenuItem key={user.id} value={user}>
                        {user.fullNameOrEmail}
                      </MenuItem>
                    ))}
                </Select>
              </Grid>
            </Grid>
            <Grid
              container
              item
              direction="column"
              xs={6}
              className="event_public_notes"
            >
              <Grid item>
                <div className="field">
                  <label
                    htmlFor={
                      publicNotesTabValue === 0
                        ? "public_notes_en"
                        : "public_notes_es"
                    }
                  >
                    {t("shiftForm.publicNotesHeader")}
                  </label>
                </div>
              </Grid>
              <div className="tabbed-field">
                <Grid item>
                  <Tabs
                    value={publicNotesTabValue}
                    onChange={(event, newValue) => {
                      setPublicNotesTabValue(newValue);
                    }}
                  >
                    <Tab label={t("shiftForm.englishTab")} />
                    <Tab label={t("shiftForm.spanishTab")} />
                  </Tabs>
                </Grid>
                <Grid item>
                  <div
                    className={`tabs__content ${publicNotesTabValue === 0 && "tabs__content--active"}`}
                  >
                    <div className="field">
                      <trix-editor
                        ref={publicNotesEnField}
                        id="public_notes_en"
                      />
                    </div>
                  </div>
                  <div
                    className={`tabs__content ${publicNotesTabValue === 1 && "tabs__content--active"}`}
                  >
                    <div className="field">
                      <trix-editor
                        ref={publicNotesEsField}
                        id="public_notes_es"
                      />
                    </div>
                  </div>
                </Grid>
              </div>
            </Grid>
            <Grid
              container
              item
              direction="column"
              xs={6}
              className="event_admin_notes"
            >
              <Grid item>
                <div className="field">
                  <label
                    htmlFor={
                      adminNotesTabValue === 0
                        ? "admin_notes_en"
                        : "admin_notes_es"
                    }
                  >
                    {t("shiftForm.adminNotesHeader")}
                  </label>
                </div>
              </Grid>
              <div className="tabbed-field">
                <Grid item>
                  <Tabs
                    value={adminNotesTabValue}
                    onChange={(event, newValue) => {
                      setAdminNotesTabValue(newValue);
                    }}
                  >
                    <Tab label={t("shiftForm.englishTab")} />
                    <Tab label={t("shiftForm.spanishTab")} />
                  </Tabs>
                </Grid>
                <Grid item>
                  <div
                    className={`tabs__content ${adminNotesTabValue === 0 && "tabs__content--active"}`}
                  >
                    <div className="field">
                      <trix-editor
                        ref={adminNotesEnField}
                        id="admin_notes_en"
                      />
                    </div>
                  </div>
                  <div
                    className={`tabs__content ${adminNotesTabValue === 1 && "tabs__content--active"}`}
                  >
                    <div className="field">
                      <trix-editor
                        ref={adminNotesEsField}
                        id="admin_notes_es"
                      />
                    </div>
                  </div>
                </Grid>
              </div>
            </Grid>
            <Grid item xs={8}>
              <Grid item xs={4}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disableElevation
                  disabled={!name || submitDisabled}
                >
                  {t("global.submit")}
                </Button>
                <Button
                  onClick={onCancel}
                  disableElevation
                  style={{
                    backgroundColor: "transparent",
                    marginLeft: "0.5em",
                  }}
                >
                  {t("global.cancel")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default connectShift(ShiftForm);
